import Notification from '../../core/Notification';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BrightnessFourIcon from '@material-ui/icons/Brightness4';
import GetAppIcon from '@material-ui/icons/GetApp';
import HistoryIcon from '@material-ui/icons/History';
import InsertChartIcon from '@material-ui/icons/InsertChartOutlined';
import LinearScaleIcon from '@material-ui/icons/LinearScale';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ComponentBody from '../../core/ComponentBody';
import ComponentDetail from '../../core/ComponentDetail';
import HistoryViewer from '../../core/HistoryViewer';
import HierarchyIcon from '../../core/icons/Hierarchy';
import InAppBar from '../../core/InAppBar';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import TripleViewer from '../../core/TripleViewer';
import withGS from '../../core/withGShoc';
import SchemeExport from './SchemeExport';
import SchemeQuality from './SchemeQuality';
import SchemeStatistics from './SchemeStatistics';
import LifeCycle from '../../application-core/Lifecycle';
import TemplateValidation from '../../application-core/TemplateValidation';
import Associations from '../../core/Associations';
import FindOntologyRelatedDataDualModePassthroughDialog from '../../data/FindOntologyRelatedDataDualModePassthroughDialog';

const defaultAppBarItems = [
  {
    icon: HierarchyIcon,
    label: "Detail"
  },
  {
    icon: LinearScaleIcon,
    label: "Triples"
  },
  {
    icon: HistoryIcon,
    label: "History"
  },
  {
    icon: InsertChartIcon,
    label: "Statistics"
  },
  {
    icon: AssignmentIcon,
    label: "Quality"
  },
  {
    icon: CheckBoxIcon,
    label: "Validation"
  },
  {
    icon: GetAppIcon,
    label: "Export"
  },
  {
    icon: BrightnessFourIcon,
    label: "Lifecycle"
  }
];

class TaxonomyData extends Component {

  constructor(props) {
    super(props);
    this.state = {
      view: 0
    };
  }

  changeView = (index) => this.setState({ view: index });

  toggleTaxonomyDetail = () => this.props.toggleTaxonomyDetail(1);

  onDisable = (item, disable = true) => {
    const disabledFeatures = this.props.data.disabledFeatures;
    const name = "quality-" + item;
    if (disable && disabledFeatures && disabledFeatures.includes(name)) return;
    // Hmm, where is it!
    if (!disable && disabledFeatures && !disabledFeatures.includes(name)) return;
    if (disable) {
      this.props.onAdd('disabledFeatures', name);
    }
    else {
      this.props.onDelete('disabledFeatures', name);
    }
  };

  render() {

    const { id, data, projectIRI, graph, entitlements, ontologyProjects } = this.props;
    const { view } = this.state;

    const taxonomyLink = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + projectIRI + "&taxonomy=" + id;
    const maxWidth = [800, null, null, null, null, null, null, null, null][view];

    let tabItems = [...defaultAppBarItems];

    const appBar = <InAppBar
      tabValue={view}
      onChangeTab={this.changeView}
      tabItems={tabItems} />;

    return (
      <ProjectConsumer>
        {({ defaultLang, ontologyProjectAssociation, skosXL, hasComponent, largeDataSettings }) => (
          <ComponentBody maxWidth={maxWidth} title={data.title} id={data.id} onToggleView={this.toggleTaxonomyDetail} appBar={appBar}>
            {view === 3 && largeDataSettings &&
            <Notification dataTest="large-data-statistics" text="The large data settting is applied to this project. This may take some time to compute." />}
            {view === 6 && largeDataSettings &&
            <Notification dataTest="large-data-export" text="The large data settting is applied to this project. Exports may take a long time to complete." />}
            {view === 0 && <ComponentDetail
              id={id}
              graph={graph}
              findResourceDialog={FindOntologyRelatedDataDualModePassthroughDialog}
              batchupdateName="batchupdateTaxonomyProject"
              iriOptions={["showIRI"]}
              projectType="TaxonomyProject"
              linkedProjectTypes={[
                "https://grafsync.graphifi.com/schema/gs/TaxonomyProject",
                "https://grafsync.graphifi.com/schema/gs/DataProject"
              ]}
              entitlements={entitlements}
              baseInferredClass="http://www.w3.org/2004/02/skos/core#ConceptScheme"
              associatedProject={data.associatedProject ?
                Array.isArray(data.associatedProject) ?
                  data.associatedProject
                  :
                  [data.associatedProject]
                :
                []
              }
              entitlement={ENTITLEMENTS.EDIT_TAXONOMY}
              includeAdditionalMetadata={true}
              includeAnnotate={true}
              includeTemplates={true}
              includeLinkedResources={true}
              includeLinkedData={true}
              includeWorkflow={true}
              includeNotes={true}
              includeNotesAsResources={true}
              includeSorting={true}
              ontologyProjects={ontologyProjects}
              canHideProps={
                [                  
                  {
                    label: "Broader",
                    property: "http://www.w3.org/2004/02/skos/core#broader"
                  },
                  {
                    label: "Narrower",
                    property: "http://www.w3.org/2004/02/skos/core#narrower"
                  },
                  {
                    label: "Related",
                    property: "http://www.w3.org/2004/02/skos/core#related"
                  },
                  {
                    label: "Hidden Label",
                    property: "http://www.w3.org/2004/02/skos/core#hiddenLabel"
                  },
                  {
                    label: "Notation",
                    property: "http://www.w3.org/2004/02/skos/core#notation"
                  },
                  {
                    label: "Broad Match",
                    property: "http://www.w3.org/2004/02/skos/core#broadMatch"
                  },
                  {
                    label: "Close Match",
                    property: "http://www.w3.org/2004/02/skos/core#closeMatch"
                  },
                  {
                    label: "Exact Match",
                    property: "http://www.w3.org/2004/02/skos/core#exactMatch"
                  },
                  {
                    label: "Narrow Match",
                    property: "http://www.w3.org/2004/02/skos/core#narrowMatch"
                  },
                  {
                    label: "Related Match",
                    property: "http://www.w3.org/2004/02/skos/core#relatedMatch"
                  }, {
                    label: "Change Note",
                    property: "http://www.w3.org/2004/02/skos/core#changeNote"
                  },
                  {
                    label: "Definition",
                    property: "http://www.w3.org/2004/02/skos/core#definition"
                  },
                  {
                    label: "Editorial Note",
                    property: "http://www.w3.org/2004/02/skos/core#editorialNote"
                  },
                  {
                    label: "Example",
                    property: "http://www.w3.org/2004/02/skos/core#example"
                  },
                  {
                    label: "History Note",
                    property: "http://www.w3.org/2004/02/skos/core#historyNote"
                  },
                  {
                    label: "Scope Note",
                    property: "http://www.w3.org/2004/02/skos/core#scopeNote"
                  },
                  {
                    label: "Top Concept Of",
                    property: "http://www.w3.org/2004/02/skos/core#topConceptOf"
                  },
                  {
                    label: "In Scheme",
                    property: "http://www.w3.org/2004/02/skos/core#inScheme"
                  },
                  {
                    label: "Classes",
                    property: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
                  }
                ]}
              includeXL={skosXL}
              includeEditingMode={true}
              includeClasses={true}
              includeSlashHash={true} />
            }
            {view === 1 && <TripleViewer
              id={id}
              useWorkflow={data.activateWorkflow === true}
              requiredEntitlement={ENTITLEMENTS.EDIT_TAXONOMY}
              graph={graph}
              displayTitle={false} />}
            {view === 2 && <HistoryViewer
              id={id}
              options={[
                {
                  label: "Concept Scheme Only",
                },
                {
                  label: "All Resources In Scheme",
                  resource: true,
                  path: "http://www.w3.org/2004/02/skos/core#inScheme"
                }
              ]}
              graph={graph}
              onToggleView={this.toggleTaxonomyDetail}
              displayTitle={false} />}
            {view === 3 && <SchemeStatistics
              id={id}
              workflow={data.activateWorkflow === true}
              graph={graph} />}
            {view === 4 && <SchemeQuality
              id={id}
              skosXL={skosXL || false}
              data={data}
              entitlement={ENTITLEMENTS.EDIT_TAXONOMY}
              link={taxonomyLink}
              graph={graph}
              defaultLang={defaultLang}
              onDisable={this.onDisable}
              onToggleView={this.toggleTaxonomyDetail}
              ontologyProjectAssociation={ontologyProjectAssociation} />}
            {view === 5 && <Associations
              id={projectIRI}
              data={data}
              graph={graph}
              associatedProject={data.associatedProject ?
                Array.isArray(data.associatedProject) ?
                  data.associatedProject
                  :
                  [data.associatedProject]
                :
                []
              }
              component={TemplateValidation}
              resourceIn="http://www.w3.org/2004/02/skos/core#inScheme"
              resourceTypes={[
                "http://www.w3.org/2004/02/skos/core#Concept",
                "http://www.w3.org/2008/05/skos-xl#Label"
              ]}
              ontologyProjectAssociation={ontologyProjectAssociation}
              defaultLang={defaultLang}
              projectIRI={projectIRI}
              onToggleView={this.toggleTaxonomyDetail} />}
            {view === 6 && <SchemeExport
              id={id}
              graph={graph}
              skosXL={skosXL || false}
              sortProperty={data.sortProperty}
              sortNumerically={data.sortNumerically}
              annotations={data.activateAnnotate === true}
              examples={data.hasTopConcept ? data.hasTopConcept.slice(0, 3) : []}
              defaultLang={defaultLang}
              entitlements={entitlements}
              ontologyProjectAssociation={ontologyProjectAssociation} />}
            {view === 7 && <LifeCycle
              id={id}
              graph={graph}
              largeDataSettings={largeDataSettings}
              defaultLang={defaultLang}
              entitlements={entitlements}
              projectType="TaxonomyProject"
              skosXL={skosXL || false}
              inProp={["http://www.w3.org/2004/02/skos/core#inScheme"]}
              sortProp="prefLabel"
              labelType={[
                "http://www.w3.org/2004/02/skos/core#prefLabel",
                "http://www.w3.org/2008/05/skos-xl#literalForm"
              ]}
              // Because XL labels cannot be deprecated
              rtoDeprecated={
                [
                  {

                    "value": "http://www.w3.org/2004/02/skos/core#Concept",
                    "label": "Concept"
                  }
                ]
              }
              rto={
                skosXL ?
                  [
                    {

                      "value": "http://www.w3.org/2004/02/skos/core#Concept",
                      "label": "Concept"
                    },
                    {
                      "value": "http://www.w3.org/2008/05/skos-xl#Label",
                      "label": "XL Label"
                    }
                  ]
                  :
                  [
                    {

                      "value": "http://www.w3.org/2004/02/skos/core#Concept",
                      "label": "Concept"
                    }
                  ]
              }
              useWorkflow={data.activateWorkflow === true}
              onToggleView={this.toggleTaxonomyDetail}
              components={hasComponent}
              projectIRI={projectIRI}
              componentIRI={id} />}
          </ComponentBody>
        )}
      </ProjectConsumer>
    );
  }
}

TaxonomyData.propTypes = {
  data: PropTypes.object.isRequired,
  entitlements: PropTypes.array.isRequired,
  id: PropTypes.string.isRequired,
  projectIRI: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired,
  ontologyProjects: PropTypes.array,
  toggleTaxonomyDetail: PropTypes.func.isRequired
};

export default withRouter(withGS(TaxonomyData));